import React, { CSSProperties, forwardRef } from "react";
import cx from "classnames";
import { gql } from "utils/urql";
import Image from "components/Image";
import styles from "./ArticleInlineImageFigure.module.scss";
import {
  ArticleInlineImageAlignment,
  ArticleInlineImageFigureFragment,
  ArticleInlineImageFragment,
} from "types/api";

const ArticleInlineImageFigure = forwardRef(function ArticleInlineImageFigure(
  {
    url,
    srcSet,
    width,
    height,
    attributionText,
    altText,
    captionText,
    alignment,
    clickthroughUrl,
    sizes,
  }: ArticleInlineImageFigureFragment & {
    clickthroughUrl?: ArticleInlineImageFragment["clickthroughUrl"];
    alignment?: ArticleInlineImageAlignment | null;
    sizes?: string;
  },
  ref: <T extends unknown>(el: T) => T
) {
  const paddingBottom = `${((100 * height) / width).toFixed(2)}%`;
  // Inline images do not have attributionUrl, only attributionText or
  // captionText, depending on whether it's <figcaption class="caption">
  // or <figcaption class="credit">. The new designs don't draw a visual
  // distinction between the two.
  const caption = attributionText || captionText;

  let alignClassName: string;

  switch (alignment) {
    case ArticleInlineImageAlignment.Center:
      alignClassName = styles.alignCenter;
      break;
    case ArticleInlineImageAlignment.Left:
      alignClassName = styles.alignLeft;
      break;
    case ArticleInlineImageAlignment.Right:
      alignClassName = styles.alignRight;
      break;
    case ArticleInlineImageAlignment.Overflow:
      alignClassName = styles.alignOverflow;
      break;
    case ArticleInlineImageAlignment.Leftgutter:
      alignClassName = styles.alignLeftgutter;
      break;
    default:
      alignClassName = styles.alignWell;
      break;
  }

  interface CustomCSS extends CSSProperties {
    "--invertedImageWidth": string;
  }

  const openImage = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.open(`${clickthroughUrl}`, "primary", "width=1140,height=1000");
    return false;
  };

  const ArticlePicture = () => {
    return (
      <picture className={styles.picture} style={{ paddingBottom }}>
        <Image
          className={styles.image}
          src={url}
          srcSet={srcSet || undefined}
          width={width}
          height={height}
          alt={altText || ""}
          lazy={true}
          sizes={sizes}
        />
      </picture>
    );
  };

  return (
    <div className={cx(styles.root, alignClassName)} ref={ref}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <figure
        className={styles.figure}
        style={
          {
            "--invertedImageWidth": `-${width}px`,
            maxWidth: `${width}px`,
          } as CustomCSS
        }
      >
        {clickthroughUrl !== "" ? (
          <a href={clickthroughUrl} onClick={openImage}>
            <ArticlePicture />
          </a>
        ) : (
          <ArticlePicture />
        )}
        {caption && (
          <figcaption
            className={cx(styles.caption, alignClassName)}
            dangerouslySetInnerHTML={{ __html: caption }}
          />
        )}
      </figure>
    </div>
  );
});

ArticleInlineImageFigure.fragment = gql`
  fragment articleInlineImageFigure on Image {
    url
    width
    height
    srcSet
    attributionText
    altText
    captionText
  }
`;

export default ArticleInlineImageFigure;
